import { Component, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { HatLocationPipe } from '../../shared/hat-location.pipe';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatBadgeModule } from '@angular/material/badge';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { Organization, OrgHat, OrgSecurityGroup, Positions } from '@newgenus/common';
import { OrgBoardService } from '../../services/org-board-service';
import { OrgDialogService } from '../../services/org-dialog.service';
import { OrgHattingService } from '../../services/org-hatting.service';

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface PostHatsManagerDialogData {
    organization: Organization;
    /** The post's partKey */
    postKey: string;
    /** Display name of the post */
    postName: string;
}

interface FeatureDetail {
    key: string;
    name: string;
    permissionCount: number;
    permissions: string[];
}

/** A user currently sitting on this post + their relationship to a specific hat. */
interface HatUserChip {
    /** UID */
    key: string;
    name: string;
    initials: string;
    photoThumbnail?: string;
    photoURL?: string;
    /** assigned = on assignedToArray; pending = on pendingAssignedToArray; none = neither */
    status: 'assigned' | 'pending' | 'none';
}

interface HatRow {
    key: string;
    name: string;
    description: string;
    isSpecialRights: boolean;
    isLinked: boolean;
    userCount: number;
    pendingCount: number;
    sgCount: number;
    featureCount: number;
    permissionCount: number;
    policyCount: number;
    /** Detailed names for the expandable section */
    sgNames: string[];
    features: FeatureDetail[];
    policyNames: string[];
    /** UI: row expanded to show SG/feature/policy names */
    expanded: boolean;
    /** Position names this hat is linked to (excluding the current post) */
    otherPositionNames: string[];
    /** Search blob — name + description + sg names + feature names + position names */
    searchText: string;
    /** Fuzzy match score (lower = better); used for sort order */
    matchScore: number;
    /** Users currently on this post — shown as toggle chips on linked hats only */
    postUsers: HatUserChip[];
    /** True when at least one post user is NOT yet assigned/pending on this hat */
    hasUnassignedPostUser: boolean;
}

/**
 * Dialog: Manage which hats are linked to a single post.
 *
 *  Sections:
 *   1. Linked Hats — currently on this post (unlink button)
 *   2. Special Rights Hats — org-wide special-rights hats (link button — they
 *      aren't normally tied to posts but the user can opt-in)
 *   3. Available Hats — all other un-linked, non-special-rights hats (link)
 *
 *  The header (post info + search + linked list) is sticky; only the
 *  special-rights and available sections scroll. Search uses fuzzy
 *  subsequence matching and only narrows the *available* / *special-rights*
 *  lists — linked hats always remain visible.
 */
@Component({
    selector: 'shared-org-post-hats-manager-dialog',
    imports: [
        HatLocationPipe,
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatDividerModule,
        MatBadgeModule,
    ],
    template: `
        <h2 mat-dialog-title class="dialog-title entity-hats-bg">
            <mat-icon class="title-icon">school</mat-icon>
            <span class="title-text">Manage Hats on Post</span>
            <span class="title-total" *ngIf="totalHatCount > 0">{{ totalHatCount }} total</span>
            <button mat-icon-button mat-dialog-close class="close-button" matTooltip="Close">
                <mat-icon>close</mat-icon>
            </button>
        </h2>

        <!-- Sticky header zone (post info + search + linked list) -->
        <div class="sticky-zone">
            <div class="post-info">
                <mat-icon class="post-icon">flag</mat-icon>
                <span class="post-label">Post:</span>
                <span class="post-name">{{ data.postName }}</span>
            </div>

            <mat-form-field appearance="outline" class="search-field" subscriptSizing="dynamic">
                <mat-icon matPrefix>search</mat-icon>
                <input
                    matInput
                    placeholder="Search hats..."
                    [(ngModel)]="searchTerm"
                    (ngModelChange)="onSearchChange()"
                />
                <button *ngIf="searchTerm" mat-icon-button matSuffix (click)="clearSearch()" matTooltip="Clear">
                    <mat-icon>close</mat-icon>
                </button>
            </mat-form-field>

            <!-- Linked hats section -->
            <section class="section section-linked">
                <header class="section-header">
                    <mat-icon class="section-icon entity-hats">link</mat-icon>
                    <span class="section-title">Linked Hats</span>
                    <span class="section-count">{{ linkedHats.length }}</span>
                </header>

                <div *ngIf="linkedHats.length === 0" class="empty-state small">
                    No hats are linked to this post yet.
                </div>

                <div class="linked-list">
                    <ng-container *ngFor="let hat of linkedHats; trackBy: trackByKey">
                        <ng-container
                            *ngTemplateOutlet="hatRowTpl; context: { hat: hat, action: 'unlink' }"
                        ></ng-container>
                    </ng-container>
                </div>
            </section>
        </div>

        <!-- Scrollable content -->
        <mat-dialog-content class="dialog-content">
            <!-- Special Rights -->
            <section class="section" *ngIf="hasSpecialRights">
                <header class="section-header">
                    <mat-icon class="section-icon special">star</mat-icon>
                    <span class="section-title">Org-wide Hats carried onto this Post</span>
                    <span class="section-count"
                        >{{ filteredSpecialRights.length
                        }}<span *ngIf="searchTerm"> / {{ specialRightsHats.length }}</span></span
                    >
                    <span class="section-hint"
                        >Special-rights hats worn by users on this post — shown for reference, managed elsewhere</span
                    >
                </header>

                <div *ngIf="filteredSpecialRights.length === 0" class="empty-state small">
                    No org-wide hats match your search.
                </div>

                <ng-container *ngFor="let hat of filteredSpecialRights; trackBy: trackByKey">
                    <ng-container *ngTemplateOutlet="hatRowTpl; context: { hat: hat, action: 'link' }"></ng-container>
                </ng-container>
            </section>

            <mat-divider *ngIf="hasSpecialRights" class="section-divider"></mat-divider>

            <!-- Available -->
            <section class="section">
                <header class="section-header">
                    <mat-icon class="section-icon available">add_circle_outline</mat-icon>
                    <span class="section-title">Available Hats</span>
                    <span class="section-count"
                        >{{ filteredAvailable.length
                        }}<span *ngIf="searchTerm"> / {{ availableHats.length }}</span></span
                    >
                </header>

                <div *ngIf="filteredAvailable.length === 0" class="empty-state">
                    {{
                        searchTerm
                            ? 'No available hats match your search.'
                            : 'All non-special-rights hats are already linked.'
                    }}
                </div>

                <ng-container *ngFor="let hat of filteredAvailable; trackBy: trackByKey">
                    <ng-container *ngTemplateOutlet="hatRowTpl; context: { hat: hat, action: 'link' }"></ng-container>
                </ng-container>
            </section>
        </mat-dialog-content>

        <mat-dialog-actions align="end">
            <button mat-button mat-dialog-close>Close</button>
        </mat-dialog-actions>

        <!-- Reusable hat row template -->
        <ng-template #hatRowTpl let-hat="hat" let-action="action">
            <div class="hat-row-wrap">
                <div
                    class="hat-row"
                    [class.linked-row]="action === 'unlink'"
                    [class.special-row]="hat.isSpecialRights"
                    [class.processing]="processingKey === hat.key"
                    [class.expanded]="hat.expanded"
                >
                    <div class="hat-main">
                        <mat-icon
                            class="hat-icon"
                            [class.entity-hats]="!hat.isSpecialRights"
                            [class.special]="hat.isSpecialRights"
                            [matTooltip]="hat.isSpecialRights ? 'Special Rights hat' : 'Hat'"
                        >
                            {{ hat.isSpecialRights ? 'star' : 'school' }}
                        </mat-icon>
                        <div class="hat-text">
                            <div class="hat-name-row">
                                <span class="hat-name" [matTooltip]="hat.name">{{ hat.name }}</span>
                                <span class="meta-pill special" *ngIf="hat.isSpecialRights">Special</span>
                            </div>
                            <span
                                class="ng-hat-location"
                                *ngIf="hat.key | hatLocation: data.organization as hatWhere"
                                >{{ hatWhere }}</span
                            >
                            <div class="hat-desc" *ngIf="hat.description" [matTooltip]="hat.description">
                                {{ hat.description }}
                            </div>
                            <div class="hat-meta">
                                <span
                                    class="meta-pill"
                                    *ngIf="hat.userCount > 0"
                                    [matTooltip]="hat.userCount + ' user(s) assigned'"
                                >
                                    <mat-icon>person</mat-icon>{{ hat.userCount }}
                                </span>
                                <span
                                    class="meta-pill pending"
                                    *ngIf="hat.pendingCount > 0"
                                    [matTooltip]="hat.pendingCount + ' pending approval'"
                                >
                                    <mat-icon>schedule</mat-icon>{{ hat.pendingCount }}
                                </span>
                                <span
                                    class="meta-pill sg"
                                    *ngIf="hat.sgCount > 0"
                                    [matTooltip]="hat.sgCount + ' security group(s)'"
                                >
                                    <mat-icon>shield</mat-icon>{{ hat.sgCount }} SG
                                </span>
                                <span
                                    class="meta-pill feature"
                                    *ngIf="hat.featureCount > 0"
                                    [matTooltip]="hat.featureCount + ' feature(s) granted'"
                                >
                                    <mat-icon>extension</mat-icon>{{ hat.featureCount }} feat
                                </span>
                                <span
                                    class="meta-pill perm"
                                    *ngIf="hat.permissionCount > 0"
                                    [matTooltip]="hat.permissionCount + ' permission(s)'"
                                >
                                    <mat-icon>vpn_key</mat-icon>{{ hat.permissionCount }} perm
                                </span>
                                <span
                                    class="meta-pill policy"
                                    *ngIf="hat.policyCount > 0"
                                    [matTooltip]="hat.policyCount + ' policy/policies'"
                                >
                                    <mat-icon>gavel</mat-icon>{{ hat.policyCount }}
                                </span>
                                <span
                                    class="meta-pill positions"
                                    *ngIf="hat.otherPositionNames.length > 0"
                                    [matTooltip]="'Also linked to: ' + hat.otherPositionNames.join(', ')"
                                >
                                    <mat-icon>place</mat-icon>{{ hat.otherPositionNames.length }} other post(s)
                                </span>
                            </div>
                        </div>
                    </div>
                    <button
                        mat-icon-button
                        class="expand-btn"
                        *ngIf="hat.sgCount > 0 || hat.policyCount > 0"
                        [matTooltip]="hat.expanded ? 'Hide details' : 'Show security groups, features & policies'"
                        (click)="toggleExpand(hat); $event.stopPropagation()"
                    >
                        <mat-icon>{{ hat.expanded ? 'expand_less' : 'expand_more' }}</mat-icon>
                    </button>
                    <!--
                        Special-rights hats are org-wide and intentionally have an empty
                        positionArray. Linking them to a post would put a partKey into
                        their positionArray, which collides (same fingerprint) with the
                        post's own auto-generated post-hat and shows up in the data
                        integrity tool as a 'Duplicate Hats' error. Render a disabled,
                        informational badge instead of the link button.
                    -->
                    <span
                        *ngIf="hat.isSpecialRights && action === 'link'"
                        class="special-readonly-badge"
                        matTooltip="Special-rights hats are org-wide and can't be linked to a single post. Their permissions already apply wherever they're assigned."
                    >
                        org-wide
                    </span>
                    <button
                        *ngIf="!(hat.isSpecialRights && action === 'link')"
                        mat-icon-button
                        class="action-btn"
                        [color]="action === 'unlink' ? 'warn' : 'primary'"
                        [disabled]="processingKey === hat.key"
                        [matTooltip]="action === 'unlink' ? 'Unlink hat from post' : 'Link hat to post'"
                        (click)="action === 'unlink' ? unlinkHat(hat) : linkHat(hat)"
                    >
                        <mat-spinner *ngIf="processingKey === hat.key" diameter="18"></mat-spinner>
                        <mat-icon *ngIf="processingKey !== hat.key">{{
                            action === 'unlink' ? 'remove_circle' : 'add_circle'
                        }}</mat-icon>
                    </button>
                </div>

                <div class="hat-details" *ngIf="hat.expanded">
                    <div class="detail-block" *ngIf="hat.sgNames.length > 0">
                        <div class="detail-label">
                            <mat-icon>shield</mat-icon> Security Groups ({{ hat.sgNames.length }})
                        </div>
                        <div class="chip-list">
                            <span class="detail-chip sg" *ngFor="let n of hat.sgNames">{{ n }}</span>
                        </div>
                    </div>
                    <div class="detail-block" *ngIf="hat.features.length > 0">
                        <div class="detail-label">
                            <mat-icon>extension</mat-icon> Features &amp; Permissions ({{ hat.featureCount }} feat /
                            {{ hat.permissionCount }} perm)
                        </div>
                        <div class="chip-list">
                            <span
                                class="detail-chip feature"
                                *ngFor="let f of hat.features"
                                [matTooltip]="
                                    f.permissions.length > 0
                                        ? 'Permissions: ' + f.permissions.join(', ')
                                        : 'No permissions'
                                "
                            >
                                {{ f.name
                                }}<span class="perm-badge" *ngIf="f.permissionCount > 0">{{ f.permissionCount }}</span>
                            </span>
                        </div>
                    </div>
                    <div class="detail-block" *ngIf="hat.policyNames.length > 0">
                        <div class="detail-label">
                            <mat-icon>gavel</mat-icon> Policies ({{ hat.policyNames.length }})
                        </div>
                        <div class="chip-list">
                            <span class="detail-chip policy" *ngFor="let n of hat.policyNames">{{ n }}</span>
                        </div>
                    </div>
                    <div class="detail-empty" *ngIf="hat.sgNames.length === 0 && hat.policyNames.length === 0">
                        No security groups or policies attached.
                    </div>
                </div>

                <!-- Inline user-assignment chips.
                     For linked (post) hats: interactive — click to toggle assignment.
                     For special-rights hats: read-only status view, showing exactly
                     which users on this post carry this org-wide hat (that's the
                     whole reason it appears in this dialog). -->
                <div class="hat-users" *ngIf="(action === 'unlink' || hat.isSpecialRights) && hat.postUsers.length > 0">
                    <div class="hat-users-header">
                        <mat-icon>person</mat-icon>
                        <span class="hat-users-label">
                            {{ hat.isSpecialRights ? 'Users on this post who carry this hat' : 'Users on this post' }}
                        </span>
                        <span
                            class="approval-hint"
                            *ngIf="requireApproval && !hat.isSpecialRights"
                            matTooltip="This organization requires approval for hat changes — applies to admins too"
                        >
                            <mat-icon>info</mat-icon> approval required
                        </span>
                        <button
                            mat-button
                            color="primary"
                            class="assign-all-btn"
                            *ngIf="!hat.isSpecialRights && hat.hasUnassignedPostUser"
                            [disabled]="userProcessingKey !== null"
                            (click)="assignAllPostUsersToHat(hat); $event.stopPropagation()"
                        >
                            <mat-icon>group_add</mat-icon>
                            {{ requireApproval ? 'Request for all' : 'Assign all' }}
                        </button>
                    </div>
                    <div class="user-chip-list">
                        <button
                            type="button"
                            *ngFor="let u of hat.postUsers; trackBy: trackUserByKey"
                            class="user-chip"
                            [class.assigned]="u.status === 'assigned'"
                            [class.pending]="u.status === 'pending'"
                            [class.readonly]="hat.isSpecialRights"
                            [disabled]="hat.isSpecialRights || userProcessingKey === hat.key + ':' + u.key"
                            [matTooltip]="
                                hat.isSpecialRights ? u.name + ' carries this org-wide hat' : userChipTooltip(u)
                            "
                            (click)="!hat.isSpecialRights && toggleUserOnHat(hat, u); $event.stopPropagation()"
                        >
                            <span class="user-avatar">
                                <img
                                    *ngIf="u.photoThumbnail || u.photoURL"
                                    [src]="u.photoThumbnail || u.photoURL"
                                    alt=""
                                />
                                <span *ngIf="!(u.photoThumbnail || u.photoURL)" class="user-initials">{{
                                    u.initials
                                }}</span>
                            </span>
                            <span class="user-name">{{ u.name }}</span>
                            <mat-icon class="user-status-icon" *ngIf="userProcessingKey !== hat.key + ':' + u.key">
                                {{
                                    u.status === 'assigned'
                                        ? 'check_circle'
                                        : u.status === 'pending'
                                          ? 'schedule'
                                          : 'add_circle_outline'
                                }}
                            </mat-icon>
                            <mat-spinner
                                *ngIf="userProcessingKey === hat.key + ':' + u.key"
                                diameter="14"
                            ></mat-spinner>
                        </button>
                    </div>
                </div>
            </div>
        </ng-template>
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                min-height: 0;
                max-height: 85vh;
                --hats-color: var(--entity-color-hats, #1093ad);
                --posts-color: var(--entity-color-positions, #388e3c);
                --inset-x: 16px;
            }
            /* Kill the outer mat-dialog scroll — we manage scrolling ourselves */
            ::ng-deep .mat-mdc-dialog-surface {
                overflow: hidden !important;
                display: flex !important;
                flex-direction: column !important;
                max-height: 85vh !important;
            }
            ::ng-deep .mat-mdc-dialog-container .mdc-dialog__content {
                padding: 0 !important;
            }
            ::ng-deep .mdc-dialog__title::before {
                display: none !important;
            }
            ::ng-deep .mat-mdc-dialog-actions {
                flex-shrink: 0;
            }
            .dialog-title {
                display: flex;
                align-items: center;
                gap: 10px;
                position: relative;
                padding: 12px 56px 12px 16px !important;
                margin: 0 !important;
                background: linear-gradient(
                    135deg,
                    var(--hats-color) 0%,
                    color-mix(in srgb, var(--hats-color) 75%, #1a1a2e) 100%
                );
                color: white;
                border-radius: 4px 4px 0 0;
                flex-shrink: 0;
            }
            .dialog-title .title-icon {
                color: white;
                font-size: 26px;
                width: 26px;
                height: 26px;
            }
            .dialog-title .title-text {
                flex: 1;
                font-weight: 600;
            }
            .dialog-title .title-total {
                background: rgba(255, 255, 255, 0.2);
                padding: 2px 10px;
                border-radius: 12px;
                font-size: 12px;
                font-weight: 600;
            }
            .dialog-title .close-button {
                position: absolute;
                top: 8px;
                right: 8px;
                color: white;
            }
            .sticky-zone {
                background: white;
                padding: 12px var(--inset-x) 8px var(--inset-x);
                border-bottom: 1px solid rgba(0, 0, 0, 0.08);
                display: flex;
                flex-direction: column;
                gap: 10px;
                position: sticky;
                top: 0;
                z-index: 2;
                flex-shrink: 0;
            }
            .dialog-content {
                display: flex;
                flex-direction: column;
                gap: 14px;
                min-width: 480px;
                max-width: 100%;
                padding: 12px var(--inset-x) 36px var(--inset-x) !important;
                overflow-x: hidden;
                overflow-y: auto;
                flex: 1 1 auto;
                min-height: 0;
                max-height: none;
            }
            .post-info {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 12px;
                background: color-mix(in srgb, var(--posts-color) 8%, transparent);
                border-radius: 6px;
                font-size: 14px;
                border-left: 3px solid var(--posts-color);
            }
            .post-info .post-icon {
                color: var(--posts-color);
                font-size: 18px;
                width: 18px;
                height: 18px;
            }
            .post-info .post-label {
                font-weight: 600;
                opacity: 0.7;
            }
            .post-info .post-name {
                font-weight: 700;
            }
            .search-field {
                width: 100%;
            }
            .section {
                display: flex;
                flex-direction: column;
                gap: 6px;
            }
            .section-linked {
                background: color-mix(in srgb, var(--hats-color) 6%, transparent);
                padding: 8px 10px;
                border-radius: 6px;
                border-left: 3px solid var(--hats-color);
            }
            .section-linked .linked-list {
                display: flex;
                flex-direction: column;
                gap: 6px;
            }
            .section-header {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 4px 2px;
                font-size: 12px;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                opacity: 0.85;
            }
            .section-header .section-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
            }
            .section-header .section-icon.entity-hats {
                color: var(--hats-color);
            }
            .section-header .section-icon.special {
                color: #f57c00;
            }
            .section-header .section-icon.available {
                color: rgba(0, 0, 0, 0.55);
            }
            .section-header .section-title {
                flex-shrink: 0;
            }
            .section-header .section-count {
                background: rgba(0, 0, 0, 0.08);
                padding: 1px 8px;
                border-radius: 10px;
                font-size: 11px;
                font-weight: 600;
            }
            .section-header .section-hint {
                margin-left: auto;
                font-size: 10px;
                opacity: 0.6;
                font-weight: 500;
                font-style: italic;
                text-transform: none;
                letter-spacing: 0;
            }
            .section-divider {
                margin: 4px 0;
            }
            .empty-state {
                padding: 14px;
                text-align: center;
                font-size: 13px;
                opacity: 0.6;
                font-style: italic;
            }
            .empty-state.small {
                padding: 6px;
                font-size: 12px;
            }
            .hat-row-wrap {
                display: flex;
                flex-direction: column;
            }
            .hat-row {
                display: flex;
                align-items: center;
                gap: 4px;
                padding: 6px 8px;
                border-radius: 6px;
                border: 1px solid rgba(0, 0, 0, 0.08);
                background: white;
                transition:
                    background 0.15s,
                    border-color 0.15s,
                    box-shadow 0.15s;
                min-width: 0;
            }
            .hat-row:hover {
                background: rgba(0, 0, 0, 0.02);
                border-color: rgba(0, 0, 0, 0.18);
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
            }
            .hat-row.processing {
                opacity: 0.6;
                pointer-events: none;
            }
            .hat-row.expanded {
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
            }
            .hat-details {
                border: 1px solid rgba(0, 0, 0, 0.08);
                border-top: none;
                border-radius: 0 0 6px 6px;
                padding: 8px 10px 10px 36px;
                background: rgba(0, 0, 0, 0.02);
                display: flex;
                flex-direction: column;
                gap: 8px;
            }
            .detail-block {
                display: flex;
                flex-direction: column;
                gap: 4px;
            }
            .detail-label {
                display: flex;
                align-items: center;
                gap: 4px;
                font-size: 10px;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.4px;
                opacity: 0.7;
            }
            .detail-label mat-icon {
                font-size: 13px;
                width: 13px;
                height: 13px;
            }
            .chip-list {
                display: flex;
                flex-wrap: wrap;
                gap: 4px;
            }
            .detail-chip {
                display: inline-flex;
                align-items: center;
                gap: 3px;
                padding: 2px 8px;
                border-radius: 10px;
                font-size: 11px;
                font-weight: 600;
                background: rgba(0, 0, 0, 0.06);
            }
            .detail-chip.sg {
                background: color-mix(in srgb, #1976d2 16%, transparent);
                color: #0d47a1;
            }
            .detail-chip.feature {
                background: color-mix(in srgb, #388e3c 16%, transparent);
                color: #1b5e20;
            }
            .detail-chip.policy {
                background: color-mix(in srgb, #7b1fa2 16%, transparent);
                color: #4a148c;
            }
            .detail-chip .perm-badge {
                background: rgba(0, 0, 0, 0.18);
                color: white;
                border-radius: 8px;
                padding: 0 5px;
                font-size: 9px;
                line-height: 1.4;
            }
            .detail-empty {
                font-size: 12px;
                opacity: 0.6;
                font-style: italic;
            }
            .linked-row {
                border-left: 3px solid var(--hats-color);
                background: white;
            }
            .special-row {
                border-left: 3px solid #f57c00;
            }
            .hat-main {
                flex: 1;
                display: flex;
                align-items: flex-start;
                gap: 10px;
                min-width: 0;
            }
            .hat-icon {
                margin-top: 2px;
            }
            .hat-icon.entity-hats {
                color: var(--hats-color);
            }
            .hat-icon.special {
                color: #f57c00;
            }
            .hat-text {
                flex: 1;
                min-width: 0;
            }
            .hat-name-row {
                display: flex;
                align-items: center;
                gap: 6px;
            }
            .hat-name {
                font-weight: 600;
                font-size: 14px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 100%;
                display: inline-block;
            }
            .hat-desc {
                font-size: 12px;
                opacity: 0.7;
                font-style: italic;
                margin-top: 1px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            .hat-meta {
                display: flex;
                align-items: center;
                gap: 4px;
                margin-top: 4px;
                flex-wrap: wrap;
            }
            .meta-pill {
                display: inline-flex;
                align-items: center;
                gap: 2px;
                background: rgba(0, 0, 0, 0.06);
                padding: 1px 6px;
                border-radius: 8px;
                font-size: 10px;
                font-weight: 600;
                line-height: 1.4;
            }
            .meta-pill mat-icon {
                font-size: 11px;
                width: 11px;
                height: 11px;
            }
            .meta-pill.pending {
                background: color-mix(in srgb, #f57c00 22%, transparent);
                color: #b35900;
            }
            .meta-pill.special {
                background: color-mix(in srgb, #f57c00 22%, transparent);
                color: #b35900;
            }
            .meta-pill.sg {
                background: color-mix(in srgb, #1976d2 18%, transparent);
                color: #0d47a1;
            }
            .meta-pill.feature {
                background: color-mix(in srgb, #388e3c 18%, transparent);
                color: #1b5e20;
            }
            .meta-pill.perm {
                background: color-mix(in srgb, #00897b 18%, transparent);
                color: #004d40;
            }
            .meta-pill.policy {
                background: color-mix(in srgb, #7b1fa2 18%, transparent);
                color: #4a148c;
            }
            .meta-pill.positions {
                background: color-mix(in srgb, var(--posts-color) 18%, transparent);
                color: color-mix(in srgb, var(--posts-color) 80%, #000);
            }
            .action-btn,
            .expand-btn {
                flex-shrink: 0;
            }
            .special-readonly-badge {
                flex-shrink: 0;
                font-size: 11px;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.4px;
                color: var(--entity-color-hats, #1093ad);
                background: rgba(16, 147, 173, 0.1);
                border: 1px solid rgba(16, 147, 173, 0.35);
                padding: 4px 8px;
                border-radius: 999px;
                white-space: nowrap;
                cursor: default;
            }
            .expand-btn {
                display: inline-flex;
                align-items: center;
                justify-content: center;
            }
            .expand-btn mat-icon {
                font-size: 22px;
                width: 22px;
                height: 22px;
                line-height: 22px;
            }

            /* ──────────────── Inline user-assignment chips ──────────────── */
            .hat-users {
                border: 1px solid rgba(0, 0, 0, 0.08);
                border-top: none;
                border-radius: 0 0 6px 6px;
                padding: 8px 10px 10px;
                background: color-mix(in srgb, var(--posts-color) 5%, transparent);
                display: flex;
                flex-direction: column;
                gap: 6px;
            }
            .hat-users-header {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 11px;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.4px;
                opacity: 0.85;
            }
            .hat-users-header mat-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
            }
            .hat-users-header .hat-users-label {
                flex-shrink: 0;
            }
            .hat-users-header .approval-hint {
                display: inline-flex;
                align-items: center;
                gap: 3px;
                margin-left: 4px;
                color: #b35900;
                background: color-mix(in srgb, #f57c00 18%, transparent);
                padding: 1px 8px;
                border-radius: 10px;
                font-size: 10px;
                font-weight: 600;
                text-transform: none;
                letter-spacing: 0;
            }
            .hat-users-header .approval-hint mat-icon {
                font-size: 12px;
                width: 12px;
                height: 12px;
            }
            .hat-users-header .assign-all-btn {
                margin-left: auto;
                min-width: 0;
                padding: 0 8px;
                line-height: 26px;
                height: 26px;
                font-size: 11px;
                font-weight: 600;
                text-transform: none;
                letter-spacing: 0;
            }
            .hat-users-header .assign-all-btn mat-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
                margin-right: 2px;
            }
            .user-chip-list {
                display: flex;
                flex-wrap: wrap;
                gap: 5px;
            }
            .user-chip {
                display: inline-flex;
                align-items: center;
                gap: 5px;
                padding: 2px 8px 2px 2px;
                border-radius: 999px;
                border: 1px dashed rgba(0, 0, 0, 0.25);
                background: rgba(255, 255, 255, 0.8);
                cursor: pointer;
                font-size: 12px;
                font-weight: 500;
                color: rgba(0, 0, 0, 0.6);
                transition:
                    background 0.12s,
                    border-color 0.12s,
                    color 0.12s,
                    opacity 0.12s;
            }
            .user-chip:hover:not(:disabled) {
                border-color: var(--posts-color);
                color: rgba(0, 0, 0, 0.85);
            }
            .user-chip:disabled {
                opacity: 0.55;
                cursor: not-allowed;
            }
            .user-chip.readonly,
            .user-chip.readonly:disabled {
                opacity: 1;
                cursor: default;
            }
            .user-chip.assigned {
                background: color-mix(in srgb, #2e7d32 14%, white);
                border: 1px solid #2e7d32;
                color: #1b5e20;
            }
            .user-chip.pending {
                background: color-mix(in srgb, #f57c00 14%, white);
                border: 1px solid #f57c00;
                color: #b35900;
            }
            .user-chip .user-avatar {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 22px;
                height: 22px;
                border-radius: 50%;
                background: rgba(0, 0, 0, 0.12);
                color: white;
                font-size: 10px;
                font-weight: 700;
                overflow: hidden;
            }
            .user-chip .user-avatar img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
            .user-chip .user-status-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
            }
            .user-chip mat-spinner {
                margin-left: 2px;
            }

            @media (prefers-color-scheme: dark) {
                .sticky-zone,
                .hat-row,
                .linked-row {
                    background: #2a2a35;
                    border-color: rgba(255, 255, 255, 0.1);
                }
                .hat-row:hover {
                    background: #34343f;
                    border-color: rgba(255, 255, 255, 0.2);
                }
                .post-info {
                    background: rgba(255, 255, 255, 0.06);
                }
                .hat-users {
                    background: rgba(255, 255, 255, 0.04);
                    border-color: rgba(255, 255, 255, 0.1);
                }
                .user-chip {
                    background: rgba(255, 255, 255, 0.06);
                    color: rgba(255, 255, 255, 0.75);
                    border-color: rgba(255, 255, 255, 0.25);
                }
                .user-chip:hover:not(:disabled) {
                    color: white;
                }
                .user-chip.assigned {
                    color: #a5d6a7;
                }
                .user-chip.pending {
                    color: #ffcc80;
                }
            }
        `,
    ],
})
export class OrgPostHatsManagerDialogComponent implements OnInit, OnDestroy {
    private obService = inject(OrgBoardService);
    private dialogService = inject(OrgDialogService);
    private hattingService = inject(OrgHattingService);
    private toastr = inject(ToastrService);

    public organization!: Organization;
    public searchTerm = '';
    public processingKey: string | null = null;
    /** Per-user processing key in the form `${hatKey}:${userKey}` */
    public userProcessingKey: string | null = null;

    public linkedHats: HatRow[] = [];
    public specialRightsHats: HatRow[] = [];
    public availableHats: HatRow[] = [];

    public filteredSpecialRights: HatRow[] = [];
    public filteredAvailable: HatRow[] = [];

    /** Persisted set of hat keys whose detail panel is open (survives recompute) */
    private expandedKeys = new Set<string>();

    private orgSub?: Subscription;

    constructor(
        public dialogRef: MatDialogRef<OrgPostHatsManagerDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: PostHatsManagerDialogData
    ) {
        this.organization = data.organization;
    }

    public ngOnInit(): void {
        this.recompute();
        this.orgSub = this.dialogService.organization$.subscribe((org) => {
            if (org && org.key === this.data.organization.key) {
                this.organization = org;
                this.recompute();
            }
        });
    }

    public ngOnDestroy(): void {
        this.orgSub?.unsubscribe();
    }

    public get totalHatCount(): number {
        return this.linkedHats.length + this.specialRightsHats.length + this.availableHats.length;
    }

    public get hasSpecialRights(): boolean {
        return this.specialRightsHats.length > 0;
    }

    public trackByKey(_index: number, hat: HatRow): string {
        return hat.key;
    }

    public clearSearch(): void {
        this.searchTerm = '';
        this.onSearchChange();
    }

    public onSearchChange(): void {
        this.applyFilter();
    }

    public async linkHat(hat: HatRow): Promise<void> {
        // Defensive guard: never put a partKey into a special-rights hat's
        // positionArray. Doing so creates a fingerprint collision with the
        // post's own auto-generated post-hat and trips the duplicate-hats
        // data integrity check. The UI hides the link button for these hats
        // already; this guard catches any code path that bypasses the UI.
        if (hat.isSpecialRights) {
            this.toastr.warning(
                'Special-rights hats are org-wide and cannot be linked to a single post.',
                'Not linked'
            );
            return;
        }
        await this.updateHatPositionArray(hat, /*link*/ true);
    }

    public async unlinkHat(hat: HatRow): Promise<void> {
        await this.updateHatPositionArray(hat, /*link*/ false);
    }

    /** True when the org requires approval for hat assignment changes. Applies to admins too. */
    public get requireApproval(): boolean {
        return !!this.organization?.settings?.requireHatAssignmentApproval;
    }

    public trackUserByKey(_index: number, u: HatUserChip): string {
        return u.key;
    }

    public userChipTooltip(u: HatUserChip): string {
        if (u.status === 'assigned') {
            return this.requireApproval
                ? `${u.name} — assigned. Click to request removal (approval required).`
                : `${u.name} — assigned. Click to remove.`;
        }
        if (u.status === 'pending') {
            return `${u.name} — pending approval. Click to cancel the request.`;
        }
        return this.requireApproval
            ? `${u.name} — not on this hat. Click to request assignment (approval required).`
            : `${u.name} — not on this hat. Click to assign.`;
    }

    /**
     * Toggle a single user's relationship to a hat.
     *  - none      → assign (direct or pending depending on org setting)
     *  - pending   → cancel the pending request
     *  - assigned  → remove (also clears any pending). When approval is required this still
     *                writes immediately; the org may then require a separate approval flow
     *                to fully propagate, but the user is removed from the hat membership.
     *
     * The approval rule applies to ADMINS too — there is no bypass anywhere.
     */
    public async toggleUserOnHat(hat: HatRow, user: HatUserChip): Promise<void> {
        if (this.userProcessingKey || this.processingKey) return;
        const orgId = this.organization?.key;
        if (!orgId) return;

        const procKey = `${hat.key}:${user.key}`;
        this.userProcessingKey = procKey;

        try {
            if (user.status === 'pending') {
                const res = await this.hattingService.removeUsersPendingApproval(orgId, hat.key, [user.key]);
                if (res.succeeded) {
                    this.patchPendingRemoval(hat.key, user.key);
                    this.recompute();
                    this.toastr.info(
                        `Cancelled pending request for ${user.name} on "${hat.name}"`,
                        'Pending cancelled'
                    );
                } else {
                    this.toastr.error(res.error || 'Failed to cancel pending request', 'Error');
                }
                return;
            }

            if (user.status === 'assigned') {
                const res = await this.hattingService.removeUserFromHat(orgId, hat.key, user.key);
                if (res.succeeded) {
                    this.patchAssignedRemoval(hat.key, user.key);
                    this.recompute();
                    this.toastr.success(`Removed ${user.name} from "${hat.name}"`, 'Removed');
                } else {
                    this.toastr.error(res.error || 'Failed to remove user from hat', 'Error');
                }
                return;
            }

            // status === 'none' → assign (single user, single hat)
            const res = await this.hattingService.assignUserToPosition(
                orgId,
                this.data.postKey,
                this.data.postName,
                [hat.key],
                user.key,
                this.requireApproval
            );
            if (res.succeeded) {
                if (res.pending) {
                    this.patchPendingAdd(hat.key, user.key);
                } else {
                    this.patchAssignedAdd(hat.key, user.key);
                }
                this.recompute();
            } else {
                this.toastr.error(res.error || 'Failed to assign user', 'Error');
            }
        } catch (err) {
            console.error('[PostHatsManager] toggleUserOnHat failed:', err);
            this.toastr.error('Failed to update user assignment', 'Error');
        } finally {
            this.userProcessingKey = null;
        }
    }

    /** Bulk: assign every post-user that isn't already on (or pending on) this hat. */
    public async assignAllPostUsersToHat(hat: HatRow): Promise<void> {
        if (this.userProcessingKey || this.processingKey) return;
        const orgId = this.organization?.key;
        if (!orgId) return;

        const targets = hat.postUsers.filter((u) => u.status === 'none');
        if (targets.length === 0) return;

        // Use a sentinel key so the row's spinner state shows on the bulk action
        this.userProcessingKey = `${hat.key}:*`;
        let success = 0;
        let failed = 0;

        try {
            for (const u of targets) {
                try {
                    const res = await this.hattingService.assignUserToPosition(
                        orgId,
                        this.data.postKey,
                        this.data.postName,
                        [hat.key],
                        u.key,
                        this.requireApproval
                    );
                    if (res.succeeded) {
                        success++;
                        if (res.pending) this.patchPendingAdd(hat.key, u.key);
                        else this.patchAssignedAdd(hat.key, u.key);
                    } else {
                        failed++;
                    }
                } catch (err) {
                    console.error('[PostHatsManager] bulk assign failed for user', u.key, err);
                    failed++;
                }
            }

            this.recompute();

            if (success > 0 && failed === 0) {
                this.toastr.success(
                    this.requireApproval
                        ? `Requested approval for ${success} user(s) on "${hat.name}"`
                        : `Assigned ${success} user(s) to "${hat.name}"`,
                    'Done'
                );
            } else if (success > 0 && failed > 0) {
                this.toastr.warning(`${success} succeeded, ${failed} failed`, 'Partial');
            } else {
                this.toastr.error('Failed to assign users', 'Error');
            }
        } finally {
            this.userProcessingKey = null;
        }
    }

    // ─── Local cache patching so the UI updates immediately, before the org$ stream catches up ───

    private patchPendingAdd(hatKey: string, userKey: string): void {
        const hat = (this.organization as any)?.orgHats?.[hatKey];
        if (!hat) return;
        const arr: string[] = Array.isArray(hat.pendingAssignedToArray) ? [...hat.pendingAssignedToArray] : [];
        if (!arr.includes(userKey)) arr.push(userKey);
        hat.pendingAssignedToArray = arr;
    }
    private patchPendingRemoval(hatKey: string, userKey: string): void {
        const hat = (this.organization as any)?.orgHats?.[hatKey];
        if (!hat) return;
        if (Array.isArray(hat.pendingAssignedToArray)) {
            hat.pendingAssignedToArray = hat.pendingAssignedToArray.filter((k: string) => k !== userKey);
        }
    }
    private patchAssignedAdd(hatKey: string, userKey: string): void {
        const hat = (this.organization as any)?.orgHats?.[hatKey];
        if (!hat) return;
        const arr: string[] = Array.isArray(hat.assignedToArray) ? [...hat.assignedToArray] : [];
        if (!arr.includes(userKey)) arr.push(userKey);
        hat.assignedToArray = arr;
    }
    private patchAssignedRemoval(hatKey: string, userKey: string): void {
        const hat = (this.organization as any)?.orgHats?.[hatKey];
        if (!hat) return;
        if (Array.isArray(hat.assignedToArray)) {
            hat.assignedToArray = hat.assignedToArray.filter((k: string) => k !== userKey);
        }
        if (Array.isArray(hat.pendingAssignedToArray)) {
            hat.pendingAssignedToArray = hat.pendingAssignedToArray.filter((k: string) => k !== userKey);
        }
    }

    public toggleExpand(hat: HatRow): void {
        hat.expanded = !hat.expanded;
        if (hat.expanded) {
            this.expandedKeys.add(hat.key);
        } else {
            this.expandedKeys.delete(hat.key);
        }
    }

    // ────────────────────────────────────────────────────────────

    private recompute(): void {
        const linked: HatRow[] = [];
        const special: HatRow[] = [];
        const available: HatRow[] = [];

        const orgHats = this.organization?.orgHats as Record<string, unknown> | undefined;
        const hatKeys: string[] = (orgHats?.['hatsKeys'] as string[]) || [];

        // ── Compute the set of users currently on this post: union of assigned + pending
        //    across every hat whose positionArray includes this post.
        const postUserKeys = new Set<string>();
        for (const hk of hatKeys) {
            const h = orgHats?.[hk] as any;
            if (!h || h.isSoftDeleted) continue;
            const posArr: string[] = Array.isArray(h.positionArray) ? h.positionArray : [];
            if (!posArr.includes(this.data.postKey)) continue;
            (h.assignedToArray || []).forEach((uid: string) => postUserKeys.add(uid));
            (h.pendingAssignedToArray || []).forEach((uid: string) => postUserKeys.add(uid));
        }

        const orgUsersMap = (this.organization as any)?.orgUsers as Record<string, any> | undefined;
        const postUsersBase: Omit<HatUserChip, 'status'>[] = [];
        for (const uid of postUserKeys) {
            const ou = orgUsersMap?.[uid];
            const name: string = ou?.name || ou?.userName || uid;
            postUsersBase.push({
                key: uid,
                name,
                initials: this.buildInitials(name),
                photoThumbnail: ou?.photoThumbnail,
                photoURL: ou?.photoURL,
            });
        }
        postUsersBase.sort((a, b) => a.name.localeCompare(b.name));

        // Build position-name lookup for disambiguation
        const partsByKey = new Map<string, any>();
        (this.organization?.orgParts || []).forEach((p: any) => {
            const k = p.partKey || p.key;
            if (k) partsByKey.set(k, p);
        });

        // Build SG -> features lookup so we can compute total feature count per hat
        const orgSgs = this.organization?.orgSecurityGroups as Record<string, unknown> | undefined;
        const sgFeatures = new Map<string, string[]>();
        const sgNames = new Map<string, string>();
        const sgKeysAll: string[] = (orgSgs?.['securityGroupsKeys'] as string[]) || [];
        for (const sgKey of sgKeysAll) {
            const sg = orgSgs?.[sgKey] as OrgSecurityGroup | undefined;
            if (sg) {
                if (Array.isArray(sg.features)) sgFeatures.set(sgKey, sg.features);
                if (sg.name) sgNames.set(sgKey, sg.name);
            }
        }

        // Build feature lookup (key -> { name, permissions })
        const orgFeatures = (this.organization as any)?.orgFeatures as Record<string, unknown> | undefined;
        const featureLookup = new Map<string, { name: string; permissions: string[] }>();
        const featureKeysAll: string[] = (orgFeatures?.['featuresKeys'] as string[]) || [];
        for (const fk of featureKeysAll) {
            const f = orgFeatures?.[fk] as { name?: string; permissions?: string[] } | undefined;
            if (f) {
                featureLookup.set(fk, {
                    name: f.name || fk,
                    permissions: Array.isArray(f.permissions) ? f.permissions : [],
                });
            }
        }

        // Build policy lookup (key -> name)
        const orgPolicies = (this.organization as any)?.orgPolicies as Record<string, unknown> | undefined;
        const policyNames = new Map<string, string>();
        const policyKeysAll: string[] = (orgPolicies?.['policiesKeys'] as string[]) || [];
        for (const pk of policyKeysAll) {
            const p = orgPolicies?.[pk] as { name?: string } | undefined;
            if (p?.name) policyNames.set(pk, p.name);
        }

        for (const hatKey of hatKeys) {
            const hat = orgHats?.[hatKey] as
                | (OrgHat & { isSoftDeleted?: boolean; pendingAssignedToArray?: string[]; assignedToArray?: string[] })
                | undefined;
            if (!hat) continue;
            if (hat.isSoftDeleted) continue;

            const positionArray: string[] = Array.isArray((hat as any).positionArray) ? (hat as any).positionArray : [];
            const isLinked = positionArray.includes(this.data.postKey);
            const isSpecial = !!(hat as any).isSpecialRights;

            const sgKeyList: string[] = this.extractSgKeys(hat);
            const featureKeySet = new Set<string>();
            for (const sk of sgKeyList) {
                const feats = sgFeatures.get(sk);
                if (feats) feats.forEach((fk) => featureKeySet.add(fk));
            }

            // Resolve names
            const sgNamesList = sgKeyList.map((k) => sgNames.get(k) || k).sort((a, b) => a.localeCompare(b));
            const features: FeatureDetail[] = Array.from(featureKeySet)
                .map((fk) => {
                    const info = featureLookup.get(fk);
                    const perms = info?.permissions || [];
                    return {
                        key: fk,
                        name: info?.name || fk,
                        permissionCount: perms.length,
                        permissions: perms,
                    };
                })
                .sort((a, b) => a.name.localeCompare(b.name));
            const permissionCount = features.reduce((sum, f) => sum + f.permissionCount, 0);

            const policyKeyList = hat.policyKeys ? Object.keys(hat.policyKeys) : [];
            const policyNamesList = policyKeyList
                .map((k) => policyNames.get(k) || k)
                .sort((a, b) => a.localeCompare(b));

            const otherPositionNames = positionArray
                .filter((k) => k !== this.data.postKey)
                .map((k) => partsByKey.get(k)?.name)
                .filter((n): n is string => !!n);

            const name = hat.name || 'Unnamed Hat';
            const description = hat.description || '';
            const searchText = [
                name,
                description,
                ...sgNamesList,
                ...features.map((f) => f.name),
                ...policyNamesList,
                ...otherPositionNames,
            ]
                .join(' ')
                .toLowerCase();

            const row: HatRow = {
                key: hatKey,
                name,
                description,
                isSpecialRights: isSpecial,
                isLinked,
                userCount: Array.isArray(hat.assignedToArray) ? hat.assignedToArray.length : 0,
                pendingCount: Array.isArray(hat.pendingAssignedToArray) ? hat.pendingAssignedToArray.length : 0,
                sgCount: sgKeyList.length,
                featureCount: features.length,
                permissionCount,
                policyCount: policyKeyList.length,
                sgNames: sgNamesList,
                features,
                policyNames: policyNamesList,
                expanded: this.expandedKeys.has(hatKey),
                otherPositionNames,
                searchText,
                matchScore: 0,
                postUsers: [],
                hasUnassignedPostUser: false,
            };

            if (isLinked) {
                // Only linked hats get per-user toggle chips.
                const assignedSet = new Set<string>(Array.isArray(hat.assignedToArray) ? hat.assignedToArray : []);
                const pendingSet = new Set<string>(
                    Array.isArray(hat.pendingAssignedToArray) ? hat.pendingAssignedToArray : []
                );
                row.postUsers = postUsersBase.map((u) => ({
                    ...u,
                    status: assignedSet.has(u.key) ? 'assigned' : pendingSet.has(u.key) ? 'pending' : 'none',
                }));
                row.hasUnassignedPostUser = row.postUsers.some((u) => u.status === 'none');
                linked.push(row);
            } else if (isSpecial) {
                // Only surface a special-rights hat on this post dialog when
                // at least one user currently sitting on this post actually
                // wears it (assigned or pending). Special-rights hats apply
                // org-wide, so showing ones nobody on this post carries
                // would just be noise — the Hat Explorer is the place to
                // browse all of them.
                const assignedSet = new Set<string>(Array.isArray(hat.assignedToArray) ? hat.assignedToArray : []);
                const pendingSet = new Set<string>(
                    Array.isArray(hat.pendingAssignedToArray) ? hat.pendingAssignedToArray : []
                );
                const intersecting = postUsersBase.filter((u) => assignedSet.has(u.key) || pendingSet.has(u.key));
                if (intersecting.length === 0) continue;
                row.postUsers = intersecting.map((u) => ({
                    ...u,
                    status: assignedSet.has(u.key) ? 'assigned' : 'pending',
                }));
                special.push(row);
            } else {
                available.push(row);
            }
        }

        const byName = (a: HatRow, b: HatRow) => a.name.localeCompare(b.name);
        linked.sort(byName);
        special.sort(byName);
        available.sort(byName);

        this.linkedHats = linked;
        this.specialRightsHats = special;
        this.availableHats = available;

        this.applyFilter();
    }

    private buildInitials(name: string): string {
        const parts = (name || '').trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return '?';
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }

    /**
     * Read SG keys off a hat row. Tolerates the legacy mixed shape where
     * `securityGroupsArray` may contain either string keys or full SG objects.
     */
    private extractSgKeys(hat: OrgHat): string[] {
        const out: string[] = [];
        const arr = (hat as any).securityGroupsArray;
        if (Array.isArray(arr)) {
            for (const entry of arr) {
                if (typeof entry === 'string') {
                    out.push(entry);
                } else if (entry && typeof entry === 'object' && entry.key) {
                    out.push(entry.key);
                }
            }
        }
        if (out.length === 0 && Array.isArray(hat.securityGroups)) {
            for (const sg of hat.securityGroups) {
                if (sg?.key) out.push(sg.key);
            }
        }
        return Array.from(new Set(out));
    }

    private applyFilter(): void {
        const term = this.searchTerm.trim().toLowerCase();

        if (!term) {
            this.filteredSpecialRights = this.specialRightsHats;
            this.filteredAvailable = this.availableHats;
            return;
        }

        // Fuzzy filter — does NOT touch linked hats (per requirement)
        this.filteredSpecialRights = this.fuzzyFilter(this.specialRightsHats, term);
        this.filteredAvailable = this.fuzzyFilter(this.availableHats, term);
    }

    /**
     * Simple fuzzy match:
     *   1. Direct substring → score 0 (best)
     *   2. Subsequence match (each char of term appears in order in target) → score = total gap distance + 1
     *   3. No match → exclude
     * Lower score = better; results sorted by score then name.
     */
    private fuzzyFilter(rows: HatRow[], term: string): HatRow[] {
        const matched: HatRow[] = [];
        for (const row of rows) {
            const score = this.fuzzyScore(row.searchText, term);
            if (score >= 0) {
                row.matchScore = score;
                matched.push(row);
            }
        }
        matched.sort((a, b) => a.matchScore - b.matchScore || a.name.localeCompare(b.name));
        return matched;
    }

    private fuzzyScore(target: string, term: string): number {
        if (!term) return 0;
        if (target.includes(term)) return 0;

        let ti = 0;
        let lastIdx = -1;
        let totalGap = 0;
        for (const ch of term) {
            const found = target.indexOf(ch, ti);
            if (found === -1) return -1;
            if (lastIdx !== -1) totalGap += found - lastIdx - 1;
            lastIdx = found;
            ti = found + 1;
        }
        return totalGap + 1;
    }

    private async updateHatPositionArray(hat: HatRow, link: boolean): Promise<void> {
        if (this.processingKey) return;

        const orgHats = this.organization?.orgHats as Record<string, unknown> | undefined;
        const fullHat = orgHats?.[hat.key] as (OrgHat & Record<string, any>) | undefined;
        if (!fullHat) {
            this.toastr.error('Hat not found in organization', 'Error');
            return;
        }

        const currentPositionArray: string[] = Array.isArray(fullHat.positionArray) ? [...fullHat.positionArray] : [];
        const postKey = this.data.postKey;

        let nextPositionArray: string[];
        if (link) {
            if (currentPositionArray.includes(postKey)) return;
            nextPositionArray = [...currentPositionArray, postKey];
        } else {
            nextPositionArray = currentPositionArray.filter((k) => k !== postKey);
        }

        // Build matching positions[] array (denormalized snapshot)
        const partsByKey = new Map<string, any>();
        (this.organization?.orgParts || []).forEach((p: any) => {
            const k = p.partKey || p.key;
            if (k) partsByKey.set(k, p);
        });
        const nextPositions: Positions[] = nextPositionArray.map((k) => {
            const part = partsByKey.get(k);
            return {
                key: k,
                partKey: k,
                name: part?.name || k,
                colour: { background: '', color: '', linkColor: '' },
                depth: 0,
                id: 0,
                parentPartKey: '',
                parentRef: '',
                path: '',
            } as Positions;
        });

        this.processingKey = hat.key;

        try {
            const partial: Partial<OrgHat> & Record<string, any> = {
                key: hat.key,
                positionArray: nextPositionArray,
                positions: nextPositions,
            };

            const result = await this.obService.saveHat(partial as OrgHat);

            if (result.succeeded) {
                if (fullHat) {
                    (fullHat as any).positionArray = nextPositionArray;
                    (fullHat as any).positions = nextPositions;
                }
                this.recompute();
                this.toastr.success(
                    link
                        ? `Linked "${hat.name}" to ${this.data.postName}`
                        : `Unlinked "${hat.name}" from ${this.data.postName}`,
                    'Updated'
                );
            } else {
                this.toastr.error('Failed to update hat', 'Error');
            }
        } catch (err) {
            console.error('[PostHatsManager] Failed to update hat:', err);
            this.toastr.error('Failed to update hat', 'Error');
        } finally {
            this.processingKey = null;
        }
    }
}
